// frontend/openaiModal.jsx
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import {
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { Visibility, VisibilityOff, Add } from "@material-ui/icons";
import { getTokens } from "../../styles/tokens";
import {
  usePanelStyles,
  ModalTitle,
  FormField,
  FormGrid,
  FormSection,
} from "../QueueIntegrationModal/panelKit";

// Lista de modelos OpenAI suportados
const openAiModels = [
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106", 
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "gpt-4o-mini"
];

// Lista de vozes disponíveis para OpenAI
const availableVoices = [
  "texto",
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural"
];

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    modes: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    mode: {
      margin: 0,
      alignItems: "flex-start",
      padding: "8px 12px 10px 4px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.paper,
      "&:hover": { borderColor: t.lineStrong },
    },
    modeSelected: {
      borderColor: t.brand,
      backgroundColor: t.tint.soft,
      "&:hover": { borderColor: t.brand },
    },
    modeTitle: {
      display: "block",
      paddingTop: 8,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
    },
    modeHelp: {
      display: "block",
      marginTop: 2,
      fontSize: 12.5,
      lineHeight: 1.45,
      color: t.textSecondary,
    },
    temporary: {
      marginTop: 14,
      padding: 14,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
    },
    temporaryTitle: {
      margin: 0,
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
    },
    temporaryHelp: {
      margin: "2px 0 12px",
      fontSize: 12.5,
      lineHeight: 1.45,
      color: t.textSecondary,
    },
    keywordRow: { display: "flex", alignItems: "center", gap: 8 },
    keywords: { display: "flex", flexWrap: "wrap", gap: 6 },
    check: {
      margin: 0,
      alignItems: "flex-start",
      "& .MuiCheckbox-root": { padding: 6, marginLeft: -6, marginRight: 4 },
    },
  };
});

// Esquema de validação para OpenAI
const OpenAiSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(openAiModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  apiKey: Yup.string().required("Informe a API Key"),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  voice: Yup.string().required("Informe o modo para Voz"),
  voiceKey: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Key é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  voiceRegion: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Region é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
  maxInteractions: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 interação")
      .max(50, "Máximo 50 interações")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  completionTimeout: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 minuto")
      .max(60, "Máximo 60 minutos")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  continueKeywords: Yup.array().when("flowMode", {
    is: "temporary",
    then: Yup.array()
      .of(Yup.string().required("Palavra-chave não pode estar vazia"))
      .min(1, "Pelo menos uma palavra-chave é obrigatória no modo temporário"),
    otherwise: Yup.array()
  }),
  objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
    is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
    then: Yup.string().required("Objetivo é obrigatório quando auto completar está ativo"),
    otherwise: Yup.string()
  })
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-4o",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 1000,
    temperature: 0.7,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    
    // Campos para controle de fluxo
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar OpenAI ao fluxo",
    btn: "Adicionar",
  });
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar OpenAI do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: openAiModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-4o",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || ["continuar", "próximo", "avançar"],
        maxInteractions: typebotIntegration.maxInteractions || 5,
        completionTimeout: typebotIntegration.completionTimeout || 10,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar OpenAI ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const promptData = {
      ...values,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      // Forçar provider como openai
      provider: "openai"
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim() && !integration.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const removeKeyword = (arrayHelpers, index) => {
    arrayHelpers.remove(index);
  };

  const getModelDisplayName = (model) => {
    const modelNames = {
      "gpt-3.5-turbo": "GPT 3.5 Turbo",
      "gpt-3.5-turbo-1106": "GPT 3.5 Turbo (1106)",
      "gpt-3.5-turbo-16k": "GPT 3.5 Turbo 16K",
      "gpt-4": "GPT 4",
      "gpt-4-turbo": "GPT 4 Turbo",
      "gpt-4o": "GPT 4o",
      "gpt-4o-mini": "GPT 4o Mini"
    };
    return modelNames[model] || model;
  };

  const getVoiceDisplayName = (voice) => {
    if (voice === "texto") return "Apenas Texto";
    return voice.replace("pt-BR-", "").replace("Neural", "");
  };

  return (
    <Dialog
      open={open === "create" || open === "edit"}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <ModalTitle
        id="form-dialog-title"
        title={labels.title}
        subtitle="Coloca um assistente de IA da OpenAI para conversar neste ponto do fluxo."
        onClose={handleClose}
      />
      <Formik
        initialValues={integration}
        enableReinitialize={true}
        validationSchema={OpenAiSchema}
        onSubmit={handleSavePrompt}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form className={panel.form}>
            <DialogContent>
              <FormSection title="Modelo e credenciais">
                <FormGrid>
                  <FormField
                    label="Nome do Assistente"
                    required
                    error={touched.name && errors.name}
                  >
                    <Field
                      as={TextField}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                    />
                  </FormField>

                  <FormField
                    label="Modelo OpenAI"
                    error={touched.model && errors.model}
                  >
                    <Field
                      as={TextField}
                      select
                      name="model"
                      error={touched.model && Boolean(errors.model)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    >
                      {openAiModels.map((model) => (
                        <MenuItem key={model} value={model}>
                          {getModelDisplayName(model)}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormField>

                  <FormField
                    full
                    label="API Key OpenAI"
                    required
                    error={touched.apiKey && errors.apiKey}
                  >
                    <Field
                      as={TextField}
                      name="apiKey"
                      type={showApiKey ? "text" : "password"}
                      autoComplete="new-password"
                      error={touched.apiKey && Boolean(errors.apiKey)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label="Mostrar ou ocultar a chave"
                              onClick={() => setShowApiKey(!showApiKey)}
                            >
                              {showApiKey ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection title="Instruções">
                <FormField
                  label="Prompt do Sistema"
                  required
                  error={touched.prompt && errors.prompt}
                >
                  <Field
                    as={TextField}
                    name="prompt"
                    error={touched.prompt && Boolean(errors.prompt)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    minRows={6}
                    maxRows={14}
                    multiline
                    placeholder="Descreva como a IA deve se comportar, que informações deve coletar, como deve responder..."
                  />
                </FormField>
              </FormSection>

              <FormSection title="Limites">
                <FormGrid columns={3}>
                  <FormField
                    label="Temperatura"
                    help="0 = conservador, 2 = criativo"
                    error={touched.temperature && errors.temperature}
                  >
                    <Field
                      as={TextField}
                      name="temperature"
                      error={touched.temperature && Boolean(errors.temperature)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{
                        step: "0.1",
                        min: "0",
                        max: "2",
                      }}
                    />
                  </FormField>
                  <FormField
                    label="Max Tokens"
                    help="Tamanho máximo da resposta"
                    error={touched.maxTokens && errors.maxTokens}
                  >
                    <Field
                      as={TextField}
                      name="maxTokens"
                      error={touched.maxTokens && Boolean(errors.maxTokens)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                    />
                  </FormField>
                  <FormField
                    label="Max Mensagens"
                    help="Histórico de contexto"
                    error={touched.maxMessages && errors.maxMessages}
                  >
                    <Field
                      as={TextField}
                      name="maxMessages"
                      error={touched.maxMessages && Boolean(errors.maxMessages)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                    />
                  </FormField>
                </FormGrid>
              </FormSection>

              <FormSection
                title="Voz"
                description="Opcional. Sem voz, o assistente responde só por texto."
              >
                <FormGrid>
                  <FormField
                    full
                    label="Tipo de Resposta"
                    error={touched.voice && errors.voice}
                  >
                    <Field
                      as={TextField}
                      select
                      name="voice"
                      error={touched.voice && Boolean(errors.voice)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    >
                      {availableVoices.map((voice) => (
                        <MenuItem key={voice} value={voice}>
                          {getVoiceDisplayName(voice)}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormField>

                  {values.voice !== "texto" && (
                    <>
                      <FormField
                        label="Voice Key (Azure)"
                        error={touched.voiceKey && errors.voiceKey}
                      >
                        <Field
                          as={TextField}
                          name="voiceKey"
                          error={touched.voiceKey && Boolean(errors.voiceKey)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="Chave do Azure Speech Service"
                        />
                      </FormField>
                      <FormField
                        label="Voice Region (Azure)"
                        error={touched.voiceRegion && errors.voiceRegion}
                      >
                        <Field
                          as={TextField}
                          name="voiceRegion"
                          error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="ex: brazilsouth"
                        />
                      </FormField>
                    </>
                  )}
                </FormGrid>
              </FormSection>

              <FormSection
                title="Comportamento do Fluxo"
                description="Configure como o OpenAI deve se comportar no fluxo"
              >
                <FormField label="Modo de Funcionamento">
                  <RadioGroup
                    name="flowMode"
                    value={values.flowMode}
                    onChange={(e) => setFieldValue("flowMode", e.target.value)}
                    className={classes.modes}
                  >
                    <FormControlLabel
                      value="permanent"
                      className={`${classes.mode} ${
                        values.flowMode === "permanent" ? classes.modeSelected : ""
                      }`}
                      control={<Radio color="primary" size="small" />}
                      label={
                        <span>
                          <span className={classes.modeTitle}>
                            Permanente - Encerrar fluxo aqui
                          </span>
                          <span className={classes.modeHelp}>
                            O usuário fica conversando com a IA até pedir transferência ou encerrar
                          </span>
                        </span>
                      }
                    />
                    <FormControlLabel
                      value="temporary"
                      className={`${classes.mode} ${
                        values.flowMode === "temporary" ? classes.modeSelected : ""
                      }`}
                      control={<Radio color="primary" size="small" />}
                      label={
                        <span>
                          <span className={classes.modeTitle}>
                            Temporário - Volta ao fluxo depois
                          </span>
                          <span className={classes.modeHelp}>
                            A IA executa uma tarefa específica e depois retorna ao fluxo normal
                          </span>
                        </span>
                      }
                    />
                  </RadioGroup>
                </FormField>

                {/* CONFIGURAÇÕES DO MODO TEMPORÁRIO */}
                {values.flowMode === "temporary" && (
                  <div className={classes.temporary}>
                    <h4 className={classes.temporaryTitle}>
                      Configurações do Modo Temporário
                    </h4>
                    <p className={classes.temporaryHelp}>
                      Configure quando a IA deve parar e retornar ao fluxo
                    </p>

                    <FormGrid>
                      {/* Limite de Interações */}
                      <FormField
                        label="Máximo de Interações"
                        help="Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"
                        error={touched.maxInteractions && errors.maxInteractions}
                      >
                        <Field
                          as={TextField}
                          name="maxInteractions"
                          error={touched.maxInteractions && Boolean(errors.maxInteractions)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />
                      </FormField>

                      {/* Timeout */}
                      <FormField
                        label="Timeout (minutos)"
                        help="Tempo limite antes de voltar ao fluxo (0 = sem limite)"
                        error={touched.completionTimeout && errors.completionTimeout}
                      >
                        <Field
                          as={TextField}
                          name="completionTimeout"
                          error={touched.completionTimeout && Boolean(errors.completionTimeout)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />
                      </FormField>

                      {/* Palavras-chave de Continuação */}
                      <FieldArray name="continueKeywords">
                        {(arrayHelpers) => (
                          <FormField
                            full
                            label="Palavras-chave para Continuar Fluxo"
                            help="Quando o usuário enviar uma dessas palavras, o fluxo continuará automaticamente"
                            error={
                              touched.continueKeywords &&
                              typeof errors.continueKeywords === "string" &&
                              errors.continueKeywords
                            }
                          >
                            <div className={classes.keywordRow}>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Digite uma palavra-chave"
                                value={newKeyword}
                                onChange={(e) => setNewKeyword(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    addKeyword(arrayHelpers, newKeyword);
                                  }
                                }}
                              />
                              <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                disabled={!newKeyword.trim()}
                              >
                                Adicionar
                              </Button>
                            </div>
                            <div className={classes.keywords}>
                              {values.continueKeywords.map((keyword, index) => (
                                <Chip
                                  key={index}
                                  label={keyword}
                                  onDelete={() => removeKeyword(arrayHelpers, index)}
                                  size="small"
                                />
                              ))}
                            </div>
                          </FormField>
                        )}
                      </FieldArray>

                      {/* Objetivo */}
                      <FormField
                        full
                        label="Objetivo da IA"
                        help="Descreva o que a IA deve completar (ex: 'Coletar nome, email e telefone')"
                        error={touched.objective && errors.objective}
                      >
                        <Field
                          as={TextField}
                          name="objective"
                          error={touched.objective && Boolean(errors.objective)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          placeholder="Ex: Coletar dados do cliente, Qualificar interesse, Diagnosticar problema..."
                        />
                      </FormField>

                      {/* Auto Completar */}
                      <FormControlLabel
                        className={`${classes.check} ${panel.full}`}
                        control={
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={values.autoCompleteOnObjective}
                            onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                            name="autoCompleteOnObjective"
                          />
                        }
                        label={
                          <span>
                            <span className={classes.modeTitle} style={{ paddingTop: 4 }}>
                              Auto completar quando atingir objetivo
                            </span>
                            <span className={classes.modeHelp}>
                              A IA analisará automaticamente se completou o objetivo e voltará ao fluxo
                            </span>
                          </span>
                        }
                      />

                      {/* Fila de Transferência */}
                      <FormField
                        full
                        label="ID da Fila (para transferência)"
                        help="ID da fila para onde transferir se usuário pedir atendente (0 = não transferir)"
                      >
                        <Field
                          as={TextField}
                          name="queueId"
                          error={touched.queueId && Boolean(errors.queueId)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0 }}
                        />
                      </FormField>
                    </FormGrid>
                  </div>
                )}
              </FormSection>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {labels.btn}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default FlowBuilderOpenAIModal;